import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Button, IconButton } from '@mui/material';
import { Add, Delete, DragIndicator } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Field } from './edit-object/design/Field';
import { Input } from './edit-object/design/Form';


interface DynamicTextFieldsProps {
  label: string;
  addButtonText: string;
  placeholder?: string;
  initialValues?: string[];
  onChange?: (values: string[]) => void;
  /**
   * 'mui' (default) renders the legacy MUI TextField with a notched
   * outline label. 'design' renders the edit-object hi-fi Field+Input
   * primitives, producing an always-visible uppercase label that matches
   * the rest of the redesigned page.
   */
  variant?: 'mui' | 'design';
}

export const DynamicTextFields = ({
  label,
  addButtonText,
  placeholder,
  initialValues = [''],
  onChange,
  variant = 'mui',
}: DynamicTextFieldsProps) => {
  const theme = useTheme();
  const [fields, setFields] = useState<string[]>(() => initialValues.length === 0 ? [] : initialValues);
  const prevInitialValuesRef = useRef<string>(JSON.stringify(initialValues));

  // Sync with initialValues when content changes (e.g., API data loaded after mount).
  // setState is intentional here: this is prop-to-state sync for late-arriving API data,
  // guarded by content comparison to prevent cascading renders.
  useEffect(() => {
    const serialized = JSON.stringify(initialValues);
    if (serialized !== prevInitialValuesRef.current && initialValues.length > 0) {
      prevInitialValuesRef.current = serialized;
      setFields(initialValues); // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [initialValues]);

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const updateField = (index: number, value: string) => {
    const newFields = [...fields];
    newFields[index] = value;
    setFields(newFields);
    // Call onChange with all fields to preserve editing state
    onChange?.(newFields);
  };

  const addField = () => {
    const newFields = [...fields, ''];
    setFields(newFields);
    // Call onChange to notify parent of new field
    onChange?.(newFields);
  };

  const removeField = (index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
    // Call onChange with remaining fields (can be empty array)
    onChange?.(newFields);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newFields = [...fields];
    const [draggedItem] = newFields.splice(draggedIndex, 1);
    newFields.splice(dropIndex, 0, draggedItem);

    setFields(newFields);
    // Preserve order after drag and drop
    onChange?.(newFields);
    setDraggedIndex(null);
  };

  return (
    <Box>
      {fields.map((field, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            opacity: draggedIndex === index ? 0.5 : 1
          }}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, index)}
        >
          <Box
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragEnd={() => setDraggedIndex(null)}
            sx={{
              cursor: 'grab',
              '&:hover': {
                '& .MuiSvgIcon-root': {
                  color: theme.palette.text.secondary
                }
              },
              '&:active': { cursor: 'grabbing' }
            }}
          >
            <DragIndicator
              sx={{
                color: theme.palette.text.disabled
              }}
            />
          </Box>
          {variant === 'design' ? (
            <Box sx={{ flexGrow: 1 }}>
              <Field label={`${label} ${index + 1}`}>
                <Input
                  aria-label={`${label} ${index + 1}`}
                  value={field}
                  onChange={(e) => updateField(index, e.target.value)}
                  placeholder={placeholder}
                />
              </Field>
            </Box>
          ) : (
            <TextField
              fullWidth
              label={`${label} ${index + 1}`}
              value={field}
              onChange={(e) => updateField(index, e.target.value)}
              placeholder={placeholder}
              variant="outlined"
            />
          )}
          <IconButton
            onClick={() => removeField(index)}
            color="error"
            size="small"
          >
            <Delete />
          </IconButton>
        </Box>
      ))}
      <Button
        startIcon={<Add />}
        onClick={addField}
        variant="outlined"
        size="small"
      >
        {addButtonText}
      </Button>
    </Box>
  );
};
